import { z } from 'zod'

import { LinkSchema, LocalizedMediaListSchema, LocalizedMediaSchema, LocalizedSchema } from './common'

// Page-builder blocks. Each maps to a recurring section on the old site — see
// reference/yuanpackaging/screenshots for the visual target.

export const ANCHOR_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const base = {
  id: z.string().min(1).max(64),
  /** Optional section id for in-page links such as `/about#equipment`. */
  anchor: z.union([z.literal(''), z.string().max(60).regex(ANCHOR_PATTERN, 'Lowercase letters, numbers and dashes')]).default(''),
}

export const HeroBlockSchema = z.object({
  ...base,
  type: z.literal('hero'),
  /**
   * fullBleed: image behind the copy · text: compact page intro · split: copy beside a rounded image ·
   * statement: large left-aligned intro with a hairline below · video: copy beside a YouTube video ·
   * centered: small centered intro (Contact).
   */
  variant: z.enum(['fullBleed', 'split', 'text', 'statement', 'video', 'centered']).default('fullBleed'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  body: LocalizedSchema,
  image: LocalizedMediaSchema,
  /** YouTube URL or video id, for the `video` variant. */
  video: z.string().max(200).default(''),
  links: z.array(LinkSchema).max(2),
})

export const SplitBlockSchema = z.object({
  ...base,
  type: z.literal('split'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  bodyHtml: LocalizedSchema,
  image: LocalizedMediaSchema,
  imageSide: z.enum(['left', 'right']).default('right'),
  background: z.enum(['ivory', 'linen', 'bone', 'ink']).default('ivory'),
  /** `bleed` fills its half edge to edge; `card` insets it with rounded corners and a soft shadow. */
  imageStyle: z.enum(['bleed', 'card']).default('bleed'),
  stats: z.array(z.object({ value: z.string().max(40), label: LocalizedSchema })).max(8).default([]),
})

export const StatsBlockSchema = z.object({
  ...base,
  type: z.literal('stats'),
  items: z.array(z.object({ value: z.string().max(40), label: LocalizedSchema })).max(8),
})

export const CardGridBlockSchema = z.object({
  ...base,
  type: z.literal('cardGrid'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  intro: LocalizedSchema.default({}),
  /** Optional image beside the intro (e.g. the capabilities section). */
  image: LocalizedMediaSchema.default({}),
  /** Show 01, 02, … on each card. */
  numbered: z.boolean().default(false),
  /** "View all" style link in the section header. */
  link: LinkSchema.nullable().default(null),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  /**
   * grid: image cards in columns · carousel: horizontally scrolling 4:3 cards ·
   * bordered: hairline-ruled cells with a per-card link · aside: intro + image beside numbered text cards ·
   * numbered: 4:3 photo, number + hairline, title, text, link · certificates: framed document scans ·
   * facts: label, claret title and text under a hairline · gallery: contained machine photos with titles ·
   * levels: ruled columns with number, tagline, text and a "you receive" list · definitions: term/definition grid.
   */
  layout: z.enum(['grid', 'carousel', 'bordered', 'aside', 'numbered', 'certificates', 'facts', 'gallery', 'levels', 'definitions']).default('grid'),
  /** Heading of each card's `points` list ("You receive"). */
  pointsHeading: LocalizedSchema.default({}),
  /** Heading of each card's `excluded` list ("Not included"). */
  excludedHeading: LocalizedSchema.default({}),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
  /** Link text under each card in the bordered layout ("Explore sector"). */
  cardLinkLabel: LocalizedSchema.default({}),
  cards: z
    .array(
      z.object({
        title: LocalizedSchema,
        body: LocalizedSchema,
        image: LocalizedMediaSchema,
        href: z.string().max(500),
        /** Small caps label above the title (facts) or issuer under it (certificates). */
        label: LocalizedSchema.default({}),
        tagline: LocalizedSchema.default({}),
        points: z.array(LocalizedSchema).max(12).default([]),
        excluded: z.array(LocalizedSchema).max(12).default([]),
      }),
    )
    .max(24),
})

export const ContentBlockSchema = z.object({
  ...base,
  type: z.literal('content'),
  bodyHtml: LocalizedSchema,
  /** Page title (the H1) of a `legal` document; rich text itself never contains an H1. */
  heading: LocalizedSchema.default({}),
  /** prose: editorial long-form · legal: plain document (privacy, terms) with its own H1, in the old site's neutral type. */
  style: z.enum(['prose', 'legal']).default('prose'),
})

export const FaqBlockSchema = z.object({
  ...base,
  type: z.literal('faq'),
  eyebrow: LocalizedSchema.default({}),
  heading: LocalizedSchema,
  /** list: one column of open-and-shut rows · columns: two-column tinted section (Contact). */
  layout: z.enum(['list', 'columns']).default('list'),
  items: z.array(z.object({ question: LocalizedSchema, answer: LocalizedSchema })).max(40),
})

export const QuotesBlockSchema = z.object({
  ...base,
  type: z.literal('quotes'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  items: z
    .array(z.object({ quote: LocalizedSchema, author: LocalizedSchema, organisation: LocalizedSchema }))
    .max(12),
})

export const ImageGridBlockSchema = z.object({
  ...base,
  type: z.literal('imageGrid'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  columns: z.union([z.literal(2), z.literal(3), z.literal(4), z.literal(6)]).default(3),
  /** tiles: square photos · logos: small partner logos in a row, continuing the section above. */
  variant: z.enum(['tiles', 'logos']).default('tiles'),
  items: z
    .array(
      z.object({
        image: LocalizedMediaSchema,
        caption: LocalizedSchema,
        href: z.string().max(500),
      }),
    )
    .max(48),
})

/**
 * Live catalogue listing; the data comes from published industries, categories and products.
 * industries: link cells to each industry · families: category cards with product counts ·
 * index: every product grouped by category.
 * In `cardLinkLabel` and `countLabel`, `{title}` and `{count}` are replaced per item.
 */
export const CatalogueBlockSchema = z.object({
  ...base,
  type: z.literal('catalogue'),
  source: z.enum(['industries', 'families', 'index']),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  body: LocalizedSchema,
  link: LinkSchema.nullable().default(null),
  cardLinkLabel: LocalizedSchema.default({}),
  countLabel: LocalizedSchema.default({}),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
  /** Extra group after the categories in the `index` listing (e.g. "Specification" links). */
  extraHeading: LocalizedSchema.default({}),
  extraLinks: z.array(LinkSchema).max(12).default([]),
})

/** Filterable project gallery ("Our Collections"). Wide items span both columns. */
export const PortfolioBlockSchema = z.object({
  ...base,
  type: z.literal('portfolio'),
  heading: LocalizedSchema,
  intro: LocalizedSchema,
  /** Label of the unfiltered tab ("All"). Tabs only show with at least one filter. */
  allLabel: LocalizedSchema.default({}),
  /** Shown beside the tabs, `{count}` replaced ("{count} works"). */
  countLabel: LocalizedSchema.default({}),
  filters: z.array(z.object({ key: z.string().min(1).max(40), label: LocalizedSchema })).max(12).default([]),
  items: z
    .array(
      z.object({
        image: LocalizedMediaSchema,
        title: LocalizedSchema,
        client: LocalizedSchema,
        tag: LocalizedSchema,
        filter: z.string().max(40),
        wide: z.boolean().default(false),
      }),
    )
    .max(60),
})

/** Copy beside a large image with numbered points (the flagship machine on About). */
export const SpotlightBlockSchema = z.object({
  ...base,
  type: z.literal('spotlight'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  image: LocalizedMediaSchema,
  sideEyebrow: LocalizedSchema,
  sideTitle: LocalizedSchema,
  /** Paragraphs separated by a blank line. */
  body: LocalizedSchema,
  points: z.array(LocalizedSchema).max(12).default([]),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
})

/**
 * Eyebrow and heading beside running copy.
 * ruled: full-width row under a hairline · callout: 7/5 split under a rule · centered: centered header, narrow body.
 */
export const ColumnsBlockSchema = z.object({
  ...base,
  type: z.literal('columns'),
  variant: z.enum(['ruled', 'callout', 'centered']).default('ruled'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  /** Paragraphs separated by a blank line. */
  body: LocalizedSchema,
  /** Label / value lines (indicative minimums); `href` turns the label into a link. */
  rows: z.array(z.object({ label: LocalizedSchema, value: LocalizedSchema, href: z.string().max(500).default('') })).max(12).default([]),
  link: LinkSchema.nullable().default(null),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
})

/** People cards and role grids, with a header and an optional button. */
export const TeamBlockSchema = z.object({
  ...base,
  type: z.literal('team'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  intro: LocalizedSchema,
  groups: z
    .array(
      z.object({
        label: LocalizedSchema,
        people: z
          .array(z.object({ image: LocalizedMediaSchema, badge: LocalizedSchema, role: LocalizedSchema, name: LocalizedSchema, bio: LocalizedSchema }))
          .max(12)
          .default([]),
        roles: z.array(z.object({ title: LocalizedSchema, body: LocalizedSchema })).max(24).default([]),
      }),
    )
    .max(6),
  link: LinkSchema.nullable().default(null),
})

/** One concept-work project: label, heading, quote and tags beside copy, then a photo grid. */
export const ShowcaseBlockSchema = z.object({
  ...base,
  type: z.literal('showcase'),
  label: LocalizedSchema,
  heading: LocalizedSchema,
  quote: LocalizedSchema,
  tags: z.array(LocalizedSchema).max(12).default([]),
  credit: LocalizedSchema,
  /** Paragraphs separated by a blank line. */
  body: LocalizedSchema,
  images: LocalizedMediaListSchema,
  background: z.enum(['ivory', 'bone']).default('ivory'),
})

/**
 * The swatch library on /materials or /finishes: a jump-link row with counts, then one section per
 * group. `groups` names each group and matches swatches by their `category` key.
 */
export const SwatchesBlockSchema = z.object({
  ...base,
  type: z.literal('swatches'),
  kind: z.enum(['material', 'finish']),
  groups: z.array(z.object({ key: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/), title: LocalizedSchema, note: LocalizedSchema })).max(20),
  /** Show the jump-link row above the groups. */
  showNav: z.boolean().default(true),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
})

/** A heading with running copy beside a ruled list of titled answers ("How to choose"). */
export const GuideBlockSchema = z.object({
  ...base,
  type: z.literal('guide'),
  heading: LocalizedSchema,
  /** Paragraphs separated by a blank line. */
  body: LocalizedSchema,
  items: z.array(z.object({ title: LocalizedSchema, body: LocalizedSchema })).max(24),
  background: z.enum(['ivory', 'linen', 'bone']).default('bone'),
})

/** Full-bleed image band. */
export const StripBlockSchema = z.object({
  ...base,
  type: z.literal('strip'),
  image: LocalizedMediaSchema,
})

export const CtaBlockSchema = z.object({
  ...base,
  type: z.literal('cta'),
  /** centered: large dark section · band: one row, heading left and button right · left: left-aligned dark section. */
  variant: z.enum(['centered', 'band', 'left']).default('centered'),
  eyebrow: LocalizedSchema.default({}),
  heading: LocalizedSchema,
  body: LocalizedSchema,
  links: z.array(LinkSchema).max(4),
})

/** Booking tools a page may embed. A host matches itself and its subdomains (`acme.setmore.com`). */
export const BOOKING_HOSTS = ['cal.com', 'calendly.com', 'calendar.google.com', 'calendar.app.google', 'tidycal.com', 'savvycal.com', 'setmore.com', 'youcanbook.me', 'zcal.co'] as const

/** True for an https link to one of `BOOKING_HOSTS`. */
export function isAllowedBookingUrl(value: string): boolean {
  try {
    const u = new URL(value)
    return u.protocol === 'https:' && !u.username && !u.password && BOOKING_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`))
  } catch {
    return false
  }
}

/** Embeds a booking calendar (Cal.com, Calendly, Google appointment pages, …) from an allow-listed host. Package flag `booking`. */
export const BookingBlockSchema = z.object({
  ...base,
  type: z.literal('booking'),
  eyebrow: LocalizedSchema,
  heading: LocalizedSchema,
  body: LocalizedSchema,
  /** Empty while the block is being drafted; a non-empty value must be an allow-listed https link. */
  url: z.union([z.literal(''), z.string().max(500).refine(isAllowedBookingUrl, 'Use an https link from a supported booking tool')]).default(''),
  /** Height of the embedded calendar in pixels. */
  height: z.number().int().min(360).max(1400).default(700),
  /** Text of the fallback link under the embed ("Open the booking page"). */
  linkLabel: LocalizedSchema.default({}),
  background: z.enum(['ivory', 'linen', 'bone']).default('ivory'),
})

export const BlockSchema = z.discriminatedUnion('type', [
  HeroBlockSchema,
  SplitBlockSchema,
  StatsBlockSchema,
  CardGridBlockSchema,
  ContentBlockSchema,
  FaqBlockSchema,
  QuotesBlockSchema,
  ImageGridBlockSchema,
  CatalogueBlockSchema,
  PortfolioBlockSchema,
  SpotlightBlockSchema,
  ColumnsBlockSchema,
  TeamBlockSchema,
  ShowcaseBlockSchema,
  StripBlockSchema,
  SwatchesBlockSchema,
  GuideBlockSchema,
  CtaBlockSchema,
  BookingBlockSchema,
])
export type Block = z.infer<typeof BlockSchema>
export type BlockType = Block['type']
export const BLOCK_TYPES = BlockSchema.options.map((o) => o.shape.type.value) as BlockType[]
